/**
 * MCP Outreach Worklist — the frontline staff console.
 *
 * Lists the program's assigned consumers; selecting one opens the encounter panel
 * (templated SMS, call logging, disposition, AI progress note). Artifacts land in
 * the member record. Supervisors review/approve on the MCP Outreach Billing page.
 */
import { useTabbedChrome } from '@/components/weavein';
import React, { useEffect, useState } from 'react';
import { Users, ChevronRight, Loader2 } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import OutreachEncounterPanel from '@/components/outreach/OutreachEncounterPanel';
import outreachBillingApi, { OutreachConsumer } from '@/services/api/outreachBilling';

interface Props {
  onNavigate?: (tab: string) => void;
}

export default function OutreachWorklistPage({ onNavigate }: Props) {
  const tabbed = useTabbedChrome();
  const [consumers, setConsumers] = useState<OutreachConsumer[]>([]);
  const [selected, setSelected] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const c = await outreachBillingApi.getConsumers();
      setConsumers(c);
      if (c.length && selected == null) setSelected(c[0].member_id);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load consumers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dispositionBadge = (d: string | null) => {
    if (!d) return <span className="text-xs text-gray-400">in outreach</span>;
    const color =
      d === 'enrolled' ? 'bg-green-100 text-green-800'
      : d === 'declined' ? 'bg-red-100 text-red-800'
      : 'bg-amber-100 text-amber-800';
    return <Badge className={color}>{d}</Badge>;
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          {!tabbed && <h1 className="font-display text-lg font-semibold tracking-[-0.015em] text-ink">MCP Outreach Worklist</h1>}
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Conduct and document outreach encounters per consumer. Supervisors review & bill on the
            {' '}
            <button className="underline" onClick={() => onNavigate?.('outreach-billing')}>MCP Billing Review</button>
            {' '}page.
          </p>
        </div>
      </div>

      {error && <div className="text-sm rounded-md px-3 py-2 bg-red-50 text-red-700 border border-red-200">{error}</div>}

      <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-4">
        {/* Roster */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base"><Users className="w-4 h-4" /> Consumers</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading && <div className="p-4 text-sm text-gray-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>}
            {!loading && consumers.length === 0 && (
              <div className="p-4 text-sm text-gray-500">No consumers found for this org.</div>
            )}
            <div className="divide-y">
              {consumers.map((c) => (
                <button
                  key={c.member_id}
                  onClick={() => setSelected(c.member_id)}
                  className={`w-full text-left px-3 py-2 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-800 ${selected === c.member_id ? 'bg-teal-50 dark:bg-teal-950/30' : ''}`}
                >
                  <div>
                    <div className="text-sm font-medium">{c.name}</div>
                    <div className="text-xs text-gray-500">{c.payer_name} · {c.touches.length} touch(es)</div>
                    <div className="mt-1">{dispositionBadge(c.disposition)}</div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-300" />
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Encounter panel */}
        <Card>
          <CardContent className="pt-6">
            {selected == null ? (
              <div className="text-sm text-gray-500">Select a consumer to document an encounter.</div>
            ) : (
              <OutreachEncounterPanel memberId={selected} onChanged={load} />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
